import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Clock } from "lucide-react";

/**
 * <TimelineImpact/> — single-line status banner telling the client whether
 * their behaviour is helping or hurting the timeline.
 *
 * Rules:
 *   - If client steps are 100% complete → "Your timeline has improved"
 *   - If any client steps remain → "This may delay your completion date"
 *   - If no client steps in the plan → render nothing
 *
 * `prevCompleted` is tracked across renders to flash a brief "Your timeline
 * has improved" message whenever the client completes a step.
 */
export default function TimelineImpact({ plan, accountability }) {
  const [flashImproved, setFlashImproved] = useState(false);
  const [lastComplete, setLastComplete] = useState(null);

  const clientSteps = (plan?.steps || []).filter((s) => s.action_kind !== "milestone");
  const total = clientSteps.length;
  const done  = clientSteps.filter((s) => s.status === "complete").length;
  const remaining = total - done;
  const allDone = total > 0 && remaining === 0;

  // Flash "improved" banner for ~6s whenever step count completes increases.
  useEffect(() => {
    if (lastComplete !== null && done > lastComplete) {
      setFlashImproved(true);
      const t = setTimeout(() => setFlashImproved(false), 6000);
      return () => clearTimeout(t);
    }
    setLastComplete(done);
  }, [done, lastComplete]);

  if (total === 0) return null;

  const expected = accountability?.expected_completion?.display
               || accountability?.expected_completion?.pessimistic
               || null;
  const confidence = accountability?.confidence?.score ?? null;

  let tone, Icon, headline, sub;
  if (flashImproved && !allDone) {
    tone = "bg-emerald-50 border-emerald-200 text-emerald-900";
    Icon = TrendingUp;
    headline = "Your timeline has improved";
    sub = `Step complete — confidence now ${confidence ?? "—"}%${expected ? ` · expected ${expected}` : ""}`;
  } else if (allDone) {
    tone = "bg-emerald-50 border-emerald-200 text-emerald-900";
    Icon = TrendingUp;
    headline = "Your timeline has improved";
    sub = `Everything we needed is in. ${expected ? `Expected completion: ${expected}.` : ""} ${confidence ? `Confidence ${confidence}%.` : ""}`;
  } else {
    tone = "bg-amber-50 border-amber-200 text-amber-900";
    Icon = TrendingDown;
    headline = "This may delay your completion date";
    sub = `${remaining} step${remaining === 1 ? "" : "s"} still need your attention. ${expected ? `Currently expected ${expected}.` : ""}`;
  }

  return (
    <div
      className={`border-l-4 ${tone} px-4 py-3 flex items-start gap-3`}
      data-testid="timeline-impact"
    >
      <Icon className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium" data-testid="timeline-impact-headline">
          {headline}
        </div>
        <div className="text-xs opacity-80 mt-0.5 flex items-center gap-2 flex-wrap">
          <Clock className="h-3 w-3" />
          {sub}
        </div>
        {!allDone && remaining > 0 && expected && (
          <div className="mt-2 text-xs flex flex-wrap gap-x-4 gap-y-1" data-testid="timeline-consequence">
            <span>
              <b className="text-emerald-700">If completed now</b> → expected {expected}
            </span>
            <span>
              <b className="text-amber-700">If delayed by 14 days</b> → expected {shiftDate(expected, 14)}
            </span>
          </div>
        )}
        <div className="text-[10px] tracking-widest uppercase opacity-60 mt-1.5">
          Updated today · based on your progress
        </div>
      </div>
    </div>
  );
}

function shiftDate(displayValue, days) {
  // Best-effort: pick the last token that looks like a date and shift it.
  // Falls back to "later" if parsing fails — never throws.
  if (!displayValue) return "—";
  try {
    const m = displayValue.match(/(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})/);
    if (!m) return "later";
    const [, d, mo, y] = m;
    const months = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
    const idx = months.indexOf(mo.slice(0, 3).toLowerCase());
    if (idx < 0) return "later";
    const dt = new Date(Number(y), idx, Number(d));
    dt.setDate(dt.getDate() + days);
    return dt.toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "later";
  }
}
